import { useOwnedFilePreviews, retainOwnedFilePreviews } from '@/shared/hooks/useOwnedFilePreviews';
import React, { useRef, useState } from 'react';
import { cn } from '@/ui/theme/ThemeContext';
import { Download, File, Loader2, Trash2, Upload } from 'lucide-react';
import { useProjectData } from '@/features/projects/ProjectDataContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { Button } from '@/ui/primitives/Button';
import { getNodeId } from '@/features/projects/MultiProjectContext';
import { log } from '@/shared/utils/logger';
import { FileService, type FileData } from '@/shared/storage/FileService';
import { AttachmentInlinePreview } from '@/shared/components/AttachmentInlinePreview';
import { trackAuditEvent } from '@/shared/audit/auditClient';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import { getAuthRuntimeSessionIdentity, hasAuthRuntimeProvider } from '@/features/auth/services/authRuntime';
import {
  formatFileSizeLabel,
  formatUploadTimestamp,
  qualityCheckLabel,
  uploadActorLabel,
} from '@/shared/files/fileDisplay';

interface FileAttachmentSectionProps {
  projectId: string;
  nodeId: string;
  nodeType: 'task' | 'group' | 'milestone' | 'document';
  canEdit?: boolean;
  readOnly?: boolean;
}

const subscribeToSession = (listener: () => void) => {
  window.addEventListener('auth-updated', listener);
  window.addEventListener('admin-view-as-updated', listener);
  window.addEventListener('auth-invalidated', listener);
  return () => {
    window.removeEventListener('auth-updated', listener);
    window.removeEventListener('admin-view-as-updated', listener);
    window.removeEventListener('auth-invalidated', listener);
  };
};

const getAttachmentOwner = () => JSON.stringify([getAuthRuntimeSessionIdentity(), getAdminViewAsUserId()]);

export const FileAttachmentSection: React.FC<FileAttachmentSectionProps> = (props) => {
  const sessionIdentity = React.useSyncExternalStore(subscribeToSession, getAttachmentOwner);
  return (
    <ScopedFileAttachmentSection
      key={JSON.stringify([props.projectId, props.nodeId, props.nodeType, sessionIdentity])}
      {...props}
      sessionIdentity={sessionIdentity}
    />
  );
};

const ScopedFileAttachmentSection: React.FC<FileAttachmentSectionProps & { sessionIdentity: string | null }> = ({
  projectId,
  nodeId: namespacedNodeId,
  nodeType,
  canEdit = true,
  readOnly = false,
  sessionIdentity,
}) => {
  const { locale, t } = useSettings();
  const nodeId = getNodeId(namespacedNodeId);
  const isDocumentNode = nodeType === 'document';
  const { batchUpdateNodes, projectData } = useProjectData();
  const linkedAttachmentIds = projectData?.nodes.find((node) =>
    getNodeId(node.id) === nodeId &&
    (node.projectId === projectId || (node.projectId == null && projectData.projectId === projectId))
  )?.attachments;
  const linkedAttachmentKey = Array.isArray(linkedAttachmentIds) ? JSON.stringify(linkedAttachmentIds) : null;
  const lastLinkedAttachmentKey = useRef(linkedAttachmentKey);
  const [files, setFiles] = useOwnedFilePreviews();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadBatchSize, setUploadBatchSize] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(false);
  const permissionRef = useRef(false);
  const requestRef = useRef(0);
  const mutationRef = useRef(false);
  const pendingSyncRef = useRef(false);
  const syncFilesRef = useRef<FileData[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  permissionRef.current = canEdit && !readOnly;
  const isCurrent = () => mountedRef.current
    && sessionIdentity === getAttachmentOwner()
    && (!hasAuthRuntimeProvider() || getAuthRuntimeSessionIdentity() !== null);
  const canMutate = () => isCurrent() && permissionRef.current;

  React.useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
    };
  }, []);

  const syncNodeAttachments = async (nextFiles: FileData[]) => {
    if (!canMutate()) return false;
    const nextIds = [...new Set(nextFiles.map((file) => file.id).filter(Boolean))];

    try {
      await batchUpdateNodes([{
        nodeId: namespacedNodeId,
        updates: { projectId, attachments: nextIds, attachmentCount: nextIds.length },
      }], { requireConfirmed: true });
      if (!canMutate()) {
        if (isCurrent()) setErrorMessage(t('Files saved. Editing permission is required to sync attachment metadata.'));
        return false;
      }
      pendingSyncRef.current = false;
      syncFilesRef.current = null;
      return true;
    } catch (error) {
      if (!isCurrent()) return false;
      log.error('FileAttachmentSection', 'Failed to sync attachment metadata:', error);
      setErrorMessage(t('Files saved, but attachment metadata could not be updated. Retry metadata sync.'));
      return false;
    }
  };

  React.useEffect(() => {
    void loadFiles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, nodeId, isDocumentNode]);

  React.useEffect(() => {
    if (lastLinkedAttachmentKey.current === linkedAttachmentKey) return;
    lastLinkedAttachmentKey.current = linkedAttachmentKey;
    if (linkedAttachmentKey !== null && !mutationRef.current && !pendingSyncRef.current) {
      void loadFiles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedAttachmentKey]);

  const loadFiles = async ({ syncNode = false } = {}) => {
    if (!isCurrent()) return false;
    const request = ++requestRef.current;
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const fileList = isDocumentNode
        ? await FileService.getDocumentFiles(projectId, nodeId)
        : await FileService.getNodeFiles(projectId, nodeId);
      if (!isCurrent() || request !== requestRef.current) { retainOwnedFilePreviews(fileList)(); return false; }
      setFiles(fileList);
      if (syncNode) {
        syncFilesRef.current = fileList;
        if (!canMutate()) {
          setErrorMessage(t('Files saved. Editing permission is required to sync attachment metadata.'));
          return false;
        }
        return await syncNodeAttachments(fileList);
      }
      return true;
    } catch (error) {
      if (!isCurrent() || request !== requestRef.current) return false;
      log.error('FileAttachmentSection', 'Failed to load files:', error);
      setErrorMessage(t('Failed to load attachments. Retry to refresh the file list.'));
      return false;
    } finally {
      if (isCurrent() && request === requestRef.current) setIsLoading(false);
    }
  };

  const retryFiles = async () => {
    if (!isCurrent() || mutationRef.current || (pendingSyncRef.current && !canMutate())) return;
    if (!syncFilesRef.current) {
      await loadFiles({ syncNode: pendingSyncRef.current });
      return;
    }
    mutationRef.current = true;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await syncNodeAttachments(syncFilesRef.current);
    } finally {
      if (isCurrent()) {
        mutationRef.current = false;
        setIsLoading(false);
      }
    }
  };

  const uploadSelectedFiles = async (selectedFiles: File[]) => {
    if (!canMutate() || mutationRef.current || pendingSyncRef.current || selectedFiles.length === 0) {
      return;
    }

    try {
      mutationRef.current = true;
      setIsUploading(true);
      setUploadBatchSize(selectedFiles.length);
      trackAuditEvent({
        eventType: 'file.upload_started',
        category: 'file',
        view: 'gantt',
        projectId,
        resourceId: nodeId,
        metadata: {
          nodeType,
          fileCount: selectedFiles.length,
          fileNames: selectedFiles.map((file) => file.name),
          fileSizes: selectedFiles.map((file) => file.size),
        },
      });

      const uploadResults = await Promise.allSettled(
        selectedFiles.map((file) => (
          isDocumentNode
            ? FileService.uploadFile(projectId, nodeId, file)
            : FileService.uploadNodeFile(projectId, nodeId, file)
        ))
      );
      // These upload results are metadata only; the following load owns display URLs.
      retainOwnedFilePreviews(uploadResults.flatMap(result => result.status === 'fulfilled' ? [result.value] : []))();
      if (!isCurrent()) return;

      const uploadedFiles = uploadResults.flatMap((result) => (
        result.status === 'fulfilled' ? [result.value] : []
      ));
      const failedUploadCount = uploadResults.length - uploadedFiles.length;
      const failedUploadMessages = uploadResults.flatMap((result, index) => {
        if (result.status === 'fulfilled') {
          return [];
        }
        const fileName = selectedFiles[index]?.name || `File ${index + 1}`;
        const message = result.reason instanceof Error ? result.reason.message : String(result.reason || t('Upload failed'));
        return [`${fileName}: ${message}`];
      });

      if (uploadedFiles.length > 0) {
        pendingSyncRef.current = true;
        if (!canMutate()) {
          setErrorMessage(t('Files saved. Editing permission is required to sync attachment metadata.'));
          return;
        }
        if (!await loadFiles({ syncNode: true })) return;
      }
      if (!canMutate()) return;
      trackAuditEvent({
        eventType: failedUploadCount > 0 ? 'file.upload_partial_or_failed' : 'file.upload_succeeded',
        category: 'file',
        view: 'gantt',
        projectId,
        resourceId: nodeId,
        metadata: {
          nodeType,
          selectedCount: selectedFiles.length,
          uploadedCount: uploadedFiles.length,
          failedCount: failedUploadCount,
          uploadedFileIds: uploadedFiles.map((file) => file.id),
          failedReasons: failedUploadMessages,
        },
      });

      if (failedUploadCount > 0) {
        const summary = uploadedFiles.length > 0
          ? t('Uploaded {{uploaded}} of {{total}} files. {{failed}} failed.', {
              uploaded: uploadedFiles.length,
              total: selectedFiles.length,
              failed: failedUploadCount,
            })
          : t('Failed to upload files');
        alert([summary, ...failedUploadMessages.slice(0, 3)].join('\n'));
      }
    } catch (error) {
      if (!canMutate()) return;
      log.error('FileAttachmentSection', 'Failed to upload files:', error);
      trackAuditEvent({
        eventType: 'file.upload_failed',
        category: 'file',
        view: 'gantt',
        projectId,
        resourceId: nodeId,
        metadata: {
          nodeType,
          fileCount: selectedFiles.length,
          error: error instanceof Error ? error.message : String(error),
        },
      });
      alert(t('Failed to upload files'));
    } finally {
      if (isCurrent()) {
        mutationRef.current = false;
        setIsUploading(false);
        setUploadBatchSize(0);
        setIsDragActive(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!canMutate() || !selectedFiles || selectedFiles.length === 0) {
      return;
    }
    trackAuditEvent({
      eventType: 'file.upload_selected',
      category: 'file',
      view: 'gantt',
      projectId,
      resourceId: nodeId,
      metadata: {
        nodeType,
        fileCount: selectedFiles.length,
      },
    });
    await uploadSelectedFiles(Array.from(selectedFiles));
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (canUpload && !isUploading) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget === event.target) {
      setIsDragActive(false);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    if (!canUpload || isUploading) {
      return;
    }

    const droppedFiles = Array.from(event.dataTransfer.files || []);
    if (droppedFiles.length === 0) {
      return;
    }

    await uploadSelectedFiles(droppedFiles);
  };

  const handleDelete = async (fileId: string) => {
    if (!canMutate() || mutationRef.current || pendingSyncRef.current) return;
    if (!confirm(t('Delete this file?'))) return;
    if (!canMutate()) return;

    try {
      mutationRef.current = true;
      setIsDeleting(true);
      if (isDocumentNode) {
        await FileService.deleteFile(projectId, nodeId, fileId);
      } else {
        await FileService.deleteNodeFile(projectId, nodeId, fileId);
      }
      if (!isCurrent()) return;
      pendingSyncRef.current = true;
      if (!canMutate()) {
        setErrorMessage(t('Files saved. Editing permission is required to sync attachment metadata.'));
        return;
      }
      await loadFiles({ syncNode: true });
    } catch (error) {
      if (!canMutate()) return;
      log.error('FileAttachmentSection', 'Failed to delete file:', error);
      alert(t('Failed to delete file'));
    } finally {
      if (isCurrent()) {
        mutationRef.current = false;
        setIsDeleting(false);
      }
    }
  };

  const handleDownload = async (file: FileData) => {
    if (!isCurrent()) return;
    try {
      if (!file.url) {
        throw new Error('File download URL is missing');
      }
      const objectUrl = await FileService.createAuthenticatedObjectUrl(file.url);
      if (!isCurrent()) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      const anchor = document.createElement('a');
      try {
        anchor.href = objectUrl;
        anchor.download = file.originalName;
        anchor.rel = 'noopener';
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
      } finally {
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch (error) {
      if (!isCurrent()) return;
      log.error('FileAttachmentSection', 'Failed to download file:', error);
      alert(t('Failed to download file'));
    }
  };

  const formatFileSize = (bytes: number): string => {
    return formatFileSizeLabel(bytes, {
      byteUnit: t('Bytes'),
      precision: 2,
      zeroLabel: t('0 Bytes'),
    });
  };

  const formatLocalizedUploadTimestamp = (value: string): string => {
    const formatted = formatUploadTimestamp(value, locale);
    return formatted === 'Unknown time' ? t('Unknown time') : formatted;
  };

  const formatLocalizedUploader = (file: FileData): string => {
    const uploader = uploadActorLabel(file);
    return uploader === 'Unknown uploader' ? t('Unknown uploader') : uploader;
  };

  const formatLocalizedQualityCheck = (file: FileData): string => {
    const label = qualityCheckLabel(file);
    if (label === 'Quality check pending' || label === 'Quality check not required') {
      return t(label);
    }

    const reviewed = /^Quality (.+) by (.+)$/.exec(label);
    if (reviewed) {
      const statusLabel = reviewed[1].replace(/\b\w/g, (character) => character.toUpperCase());
      const translatedStatus = t(statusLabel);
      return t('Quality {{status}} by {{reviewer}}', {
        status: translatedStatus === statusLabel ? reviewed[1] : translatedStatus,
        reviewer: reviewed[2],
      });
    }

    const unreviewed = /^Quality (.+)$/.exec(label);
    if (unreviewed) {
      const statusLabel = unreviewed[1].replace(/\b\w/g, (character) => character.toUpperCase());
      const translatedStatus = t(statusLabel);
      return t('Quality {{status}}', {
        status: translatedStatus === statusLabel ? unreviewed[1] : translatedStatus,
      });
    }

    return label;
  };

  const canUpload = canEdit && !readOnly;
  const canDelete = canEdit && !readOnly;
  const isBusy = isUploading || isDeleting;
  const uploadButtonLabel = isUploading
    ? (uploadBatchSize > 1 ? t('Uploading {{count}}...', { count: uploadBatchSize }) : t('Uploading...'))
    : t('Upload');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900 dark:text-notion-text-primary">
          {t('Attachments')}
        </h3>
        {canUpload && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBusy || pendingSyncRef.current}
            className="h-7 text-xs"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                {uploadButtonLabel}
              </>
            ) : (
              <>
                <Upload className="w-3 h-3 mr-1" />
                {uploadButtonLabel}
              </>
            )}
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          data-testid="task-attachments-file-input"
          className="hidden"
          onChange={handleFileSelect}
          disabled={!canUpload || isBusy || pendingSyncRef.current}
        />
      </div>

      {canUpload && (
        <div
          data-testid="task-attachments-dropzone"
          className={cn(
            "rounded-lg border border-dashed px-3 py-4 text-center transition-colors",
            isDragActive
              ? "border-zinc-400/70 bg-zinc-100 text-zinc-900 dark:border-white/20 dark:bg-white/10 dark:text-zinc-100"
              : "border-gray-300 bg-gray-50 text-gray-500 dark:border-notion-border-primary dark:bg-notion-bg-tertiary dark:text-notion-text-secondary",
            isUploading && "opacity-70"
          )}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-4 h-4" />
            <p className="text-xs font-medium">
              {isDragActive ? t('Drop files to attach') : t('Drag multiple files here or use Upload')}
            </p>
            <p className="text-[11px] opacity-80">
              {isUploading && uploadBatchSize > 0
                ? t('Uploading {{count}} file(s)...', { count: uploadBatchSize })
                : t('Multiple files are supported.')}
            </p>
          </div>
        </div>
      )}

      {errorMessage && (
        <div role="alert" className="text-sm text-red-600">
          <p>{errorMessage}</p>
          <Button onClick={() => void retryFiles()} disabled={isLoading || isBusy || (pendingSyncRef.current && !canUpload)}>
            {t('Retry')}
          </Button>
        </div>
      )}

      {isLoading && files.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-sm text-gray-500 dark:text-notion-text-secondary">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          {t('Loading...')}
        </div>
      ) : files.length === 0 && !errorMessage ? (
        <div className="flex flex-col items-center justify-center py-8 text-sm text-gray-500 dark:text-notion-text-secondary border border-dashed border-gray-300 dark:border-notion-border-primary rounded-lg">
          <File className="w-8 h-8 mb-2 opacity-50" />
          <p>{t('No files attached')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file) => {
            const hasInlinePreview = FileService.canInlinePreview(file.mimeType);
            return (
              <div
                key={file.id}
                className={cn(
                  "rounded-lg border p-2",
                  "border-gray-200 dark:border-notion-border-primary",
                  "bg-gray-50 dark:bg-notion-bg-tertiary"
                )}
              >
                {hasInlinePreview && (
                  <AttachmentInlinePreview
                    file={file}
                    className="mb-2"
                  />
                )}
                <div className="flex items-center gap-2">
                  <File className="w-4 h-4 text-gray-500 dark:text-notion-text-secondary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-notion-text-primary truncate">
                      {file.originalName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-notion-text-secondary">
                      {formatFileSize(file.size)} · {t('Uploaded {{date}} by {{user}}', {
                        date: formatLocalizedUploadTimestamp(file.uploadedAt),
                        user: formatLocalizedUploader(file),
                      })}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-notion-text-secondary">
                      {formatLocalizedQualityCheck(file)}
                    </p>
                    {(file.qualityCheck?.notes || file.metadata.qualityReviewNotes) && (
                      <p className="text-xs text-gray-500 dark:text-notion-text-secondary">
                        {t('Review note:')} {file.qualityCheck?.notes || file.metadata.qualityReviewNotes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDownload(file)}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-notion-bg-primary text-gray-600 dark:text-notion-text-secondary"
                      title={t('Download')}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(file.id)}
                        disabled={isBusy || pendingSyncRef.current}
                        className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-600 dark:text-notion-text-secondary hover:text-red-600"
                        title={t('Delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
